import React, { useState } from "react";

import moment from "moment";

import { formatNumber, formatNumericCurrency, useGetFarmAreaSummary, useUpdateFarmCreditAllotments } from "utils";

import { CarbonProgram, FarmAreaSummaryResponse } from "models";

import { AdminDescriptions, Button, Col, DatePicker, ErrorBoundary, Flex, Form, InputNumber, Row, SkeletonWrapper, Typography } from "components";

import { FarmPayout } from "pages/admin/carbonPrograms/components/FarmPayout.tsx";

const FarmCreditAllotmentsForm = ({
  areaSummary,
  carbonProgram,
  farmId,
  updateFarmCreditAllotments,
}: {
  areaSummary: FarmAreaSummaryResponse;
  carbonProgram: CarbonProgram;
  farmId: string;
  updateFarmCreditAllotments: ReturnType<typeof useUpdateFarmCreditAllotments>["mutate"];
}) => {
  const [errAllotment, setErrAllotment] = useState<number>(areaSummary.payoutAllotment.errAllotment);
  const [bonusAllotmentAmount, setBonusAllotmentAmount] = useState<number>(areaSummary.payoutAllotment.bonusAllotmentAmount);
  const [bonusAllotmentDueDate, setBonusAllotmentDueDate] = useState<moment.Moment>(moment(areaSummary.payoutAllotment.bonusAllotmentDueDate));

  const handleUpdateCreditAllotments = () => {
    updateFarmCreditAllotments({
      farmId,
      errAllotment,
      bonusAllotmentAmount,
      bonusAllotmentDueDate: bonusAllotmentDueDate.toISOString(),
    });
  };

  return (
    <ErrorBoundary>
      <Form onFinish={() => {}}>
        <Flex className="padding-100" justify="center" gap="6rem">
          <Flex align="center" gap="1rem">
            <InputNumber
              className="floating-label width-100"
              defaultValue={errAllotment}
              label="ERR Allotment"
              min={0}
              name="errAllotment"
              rules={[{ required: true, message: "An amount is mandatory" }]}
              suffix="t CO₂"
              value={errAllotment}
              onChange={(v) => setErrAllotment(v ?? 0)}
            />
            <Typography variant="paragraph-m" bold>
              Payout:{" "}
              {formatNumericCurrency(errAllotment * (carbonProgram.fieldsManagement.payouts?.removals.pointPrice ?? 0) * 1000, {
                currency: carbonProgram.currency,
                decimals: 0,
              })}
            </Typography>
          </Flex>
          <Flex align="center" gap="1rem">
            <InputNumber
              className="floating-label"
              defaultValue={bonusAllotmentAmount}
              label="Bonus Allotment"
              min={0}
              name="bonusAllotmentAmount"
              rules={[{ required: true, message: "An amount is mandatory" }]}
              suffix="t CO₂"
              value={bonusAllotmentAmount}
              onChange={(v) => setBonusAllotmentAmount(v ?? 0)}
            />
            <Typography variant="paragraph-m" bold>
              Payout:{" "}
              {formatNumericCurrency(bonusAllotmentAmount * (carbonProgram.fieldsManagement.payouts?.removals.pointPrice ?? 0) * 1000, {
                currency: carbonProgram.currency,
                decimals: 0,
              })}
            </Typography>
          </Flex>
          <DatePicker initialValue={bonusAllotmentDueDate} label="Bonus Allotment Due Date" value={bonusAllotmentDueDate} onChange={setBonusAllotmentDueDate} />

          <Button className="update-allotment-button" type="primary" onClick={handleUpdateCreditAllotments}>
            Update
          </Button>
        </Flex>
      </Form>
    </ErrorBoundary>
  );
};

export const FarmAreaSummary = ({ fieldIds, farmId, carbonProgram }: { fieldIds: string[]; farmId: string; carbonProgram?: CarbonProgram }) => {
  const { areaSummary, isLoading } = useGetFarmAreaSummary({ farmId });
  const { mutate: updateFarmCreditAllotments } = useUpdateFarmCreditAllotments({ farmId });

  return (
    <>
      <SkeletonWrapper isLoading={isLoading}>
        {carbonProgram && areaSummary && (
          <FarmCreditAllotmentsForm
            areaSummary={areaSummary}
            carbonProgram={carbonProgram}
            farmId={farmId}
            updateFarmCreditAllotments={updateFarmCreditAllotments}
          />
        )}
        <ErrorBoundary>
          <Row>
            <Col>
              <AdminDescriptions
                items={[
                  {
                    label: "Field continuation area",
                    span: 1,
                    children: areaSummary?.fieldContinuationArea ? `${formatNumber({ number: areaSummary.fieldContinuationArea })} ha` : "Unknown",
                  },
                  {
                    label: "Provided field continuation area",
                    span: 1,
                    children: areaSummary?.providedFieldContinuationArea
                      ? `${formatNumber({ number: areaSummary.providedFieldContinuationArea })} ha`
                      : "Unknown",
                  },
                  {
                    label: "Activable hectares",
                    span: 1,
                    children: areaSummary?.activatableHectares ? `${formatNumber({ number: areaSummary.activatableHectares })} ha` : "Unknown",
                  },
                  {
                    label: "Field payout",
                    span: 1,
                    children: (
                      <>
                        <FarmPayout fieldIds={fieldIds} farmId={farmId} carbonProgram={carbonProgram} />
                      </>
                    ),
                  },
                ]}
                columns={null}
              />
            </Col>
          </Row>
        </ErrorBoundary>
      </SkeletonWrapper>
    </>
  );
};
